/**
 * UnreadDividerContext
 *
 * Global context for managing "New Messages" dividers across ALL chat rooms.
 * This allows tracking unread messages even when user is in a different room.
 *
 * RULES (per CHAT_SCROLL_PROMPT.md):
 *
 * DIVIDER APPEARS WHEN:
 * - User is outside the chat (different channel/page) and new message arrives
 * - User is in chat but window/tab is in background (document.hidden === true)
 * - User is in chat, window active, but scrolled up
 *
 * DIVIDER DOES NOT APPEAR WHEN:
 * - User is in chat, window active, and at bottom (seeing messages in real-time)
 * - User sends a message themselves
 *
 * DIVIDER DISAPPEARS WHEN:
 * - User brings divider into viewport for at least 1 second
 * - User scrolls to bottom and sees all new messages
 * - User sends a message (considered as "caught up")
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";

interface RoomDividerState {
  dividerMessageId: string | null;  // Message ID where divider should appear (ABOVE this message)
  newMessagesCount: number;         // Count of new messages for floating button
  lastSeenMessageId: string | null; // Last message user definitely saw
}

interface UnreadDividerContextValue {
  // Get divider state for a specific room
  getRoomDividerState: (sessionKey: string) => RoomDividerState;

  // Handle new message arriving (call from WebSocket handler)
  handleNewMessage: (
    sessionKey: string,
    messageId: string,
    isOwnMessage: boolean,
    isUserInThisRoom: boolean,
    isUserAtBottom: boolean,
    isWindowFocused: boolean
  ) => void;

  // Handle user sending a message (clears divider)
  handleUserSentMessage: (sessionKey: string) => void;

  // Handle divider becoming visible for 1 second
  handleDividerViewed: (sessionKey: string) => void;

  // Handle user scrolling to bottom
  handleScrollToBottom: (sessionKey: string) => void;

  // Clear only the new messages count (keeps divider visible)
  clearNewMessagesCount: (sessionKey: string) => void;

  // Set initial unread message from API
  setInitialUnreadMessage: (sessionKey: string, messageId: string | null) => void;

  // Clear divider for a room
  clearDivider: (sessionKey: string) => void;
}

const UnreadDividerContext = createContext<UnreadDividerContextValue | null>(null);

export const useUnreadDividerContext = () => {
  const context = useContext(UnreadDividerContext);
  if (!context) {
    throw new Error("useUnreadDividerContext must be used within UnreadDividerProvider");
  }
  return context;
};

interface UnreadDividerProviderProps {
  children: React.ReactNode;
}

export const UnreadDividerProvider: React.FC<UnreadDividerProviderProps> = ({ children }) => {
  // Store divider state for all rooms
  const [roomStates, setRoomStates] = useState<Map<string, RoomDividerState>>(new Map());

  // Ref for 1-second visibility timers
  const visibilityTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Default state for rooms without explicit state
  const defaultState: RoomDividerState = {
    dividerMessageId: null,
    newMessagesCount: 0,
    lastSeenMessageId: null,
  };

  /**
   * Get divider state for a room
   */
  const getRoomDividerState = useCallback((sessionKey: string): RoomDividerState => {
    return roomStates.get(sessionKey) || defaultState;
  }, [roomStates]);

  /**
   * Handle new message arriving
   */
  const handleNewMessage = useCallback((
    sessionKey: string,
    messageId: string,
    isOwnMessage: boolean,
    isUserInThisRoom: boolean,
    isUserAtBottom: boolean,
    isWindowFocused: boolean
  ) => {
    console.log(`📨 UnreadDividerContext.handleNewMessage:`, {
      sessionKey,
      messageId,
      isOwnMessage,
      isUserInThisRoom,
      isUserAtBottom,
      isWindowFocused
    });

    // Own messages don't create divider
    if (isOwnMessage) {
      console.log(`📨 Own message, not creating divider`);
      return;
    }

    // Determine if divider should be created
    const shouldCreateDivider =
      !isUserInThisRoom ||   // User is in different room
      !isWindowFocused ||    // Window/tab is in background
      !isUserAtBottom;       // User scrolled up

    if (shouldCreateDivider) {
      setRoomStates(prev => {
        const newMap = new Map(prev);
        const currentState = prev.get(sessionKey) || defaultState;

        // Only set divider if not already set (keep the first unread position)
        const newDividerId = currentState.dividerMessageId || messageId;
        const newCount = currentState.newMessagesCount + 1;

        console.log(`📍 Creating/updating divider for ${sessionKey}: messageId=${newDividerId}, count=${newCount}`);

        newMap.set(sessionKey, {
          ...currentState,
          dividerMessageId: newDividerId,
          newMessagesCount: newCount,
        });

        return newMap;
      });
    } else {
      // User is actively viewing - update last seen
      console.log(`👁️ User is actively viewing ${sessionKey}, updating lastSeenMessageId`);
      setRoomStates(prev => {
        const newMap = new Map(prev);
        const currentState = prev.get(sessionKey) || defaultState;

        newMap.set(sessionKey, {
          ...currentState,
          lastSeenMessageId: messageId,
        });

        return newMap;
      });
    }
  }, []);

  /**
   * Handle user sending a message - clears divider
   */
  const handleUserSentMessage = useCallback((sessionKey: string) => {
    console.log(`✉️ User sent message in ${sessionKey}, clearing divider`);

    // Clear any pending visibility timer
    const timer = visibilityTimersRef.current.get(sessionKey);
    if (timer) {
      clearTimeout(timer);
      visibilityTimersRef.current.delete(sessionKey);
    }

    setRoomStates(prev => {
      const newMap = new Map(prev);
      const currentState = prev.get(sessionKey) || defaultState;

      newMap.set(sessionKey, {
        ...currentState,
        dividerMessageId: null,
        newMessagesCount: 0,
      });

      return newMap;
    });
  }, []);

  /**
   * Handle divider being viewed for 1 second
   */
  const handleDividerViewed = useCallback((sessionKey: string) => {
    console.log(`👁️ Divider viewed in ${sessionKey} for 1 second, clearing`);

    setRoomStates(prev => {
      const newMap = new Map(prev);
      const currentState = prev.get(sessionKey) || defaultState;

      newMap.set(sessionKey, {
        ...currentState,
        dividerMessageId: null,
        newMessagesCount: 0,
      });

      return newMap;
    });
  }, []);

  /**
   * Handle user scrolling to bottom
   */
  const handleScrollToBottom = useCallback((sessionKey: string) => {
    console.log(`⬇️ User scrolled to bottom in ${sessionKey}, clearing divider`);

    // Clear any pending visibility timer
    const timer = visibilityTimersRef.current.get(sessionKey);
    if (timer) {
      clearTimeout(timer);
      visibilityTimersRef.current.delete(sessionKey);
    }

    setRoomStates(prev => {
      const newMap = new Map(prev);
      const currentState = prev.get(sessionKey) || defaultState;

      newMap.set(sessionKey, {
        ...currentState,
        dividerMessageId: null,
        newMessagesCount: 0,
      });

      return newMap;
    });
  }, []);

  /**
   * Clear only the new messages count (for floating button)
   * This keeps the divider visible but hides the floating button
   */
  const clearNewMessagesCount = useCallback((sessionKey: string) => {

    setRoomStates(prev => {
      const newMap = new Map(prev);
      const currentState = prev.get(sessionKey) || defaultState;

      // Only update if there's actually a count to clear
      if (currentState.newMessagesCount > 0) {
        newMap.set(sessionKey, {
          ...currentState,
          newMessagesCount: 0,
        });
        return newMap;
      }

      return prev; // No change needed
    });
  }, []);

  /**
   * Set initial unread message from API
   */
  const setInitialUnreadMessage = useCallback((sessionKey: string, messageId: string | null) => {
    console.log(`🎯 Setting initial unread message for ${sessionKey}: ${messageId}`);

    setRoomStates(prev => {
      const newMap = new Map(prev);
      const currentState = prev.get(sessionKey) || defaultState;

      // Only set if we don't already have a divider (preserve existing state on room re-entry)
      if (!currentState.dividerMessageId) {
        newMap.set(sessionKey, {
          ...currentState,
          dividerMessageId: messageId,
        });
      }

      return newMap;
    });
  }, []);

  /**
   * Clear divider for a room
   */
  const clearDivider = useCallback((sessionKey: string) => {
    console.log(`🗑️ Clearing divider for ${sessionKey}`);

    // Clear any pending visibility timer
    const timer = visibilityTimersRef.current.get(sessionKey);
    if (timer) {
      clearTimeout(timer);
      visibilityTimersRef.current.delete(sessionKey);
    }

    setRoomStates(prev => {
      const newMap = new Map(prev);
      newMap.delete(sessionKey);
      return newMap;
    });
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      visibilityTimersRef.current.forEach(timer => clearTimeout(timer));
      visibilityTimersRef.current.clear();
    };
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value: UnreadDividerContextValue = React.useMemo(() => ({
    getRoomDividerState,
    handleNewMessage,
    handleUserSentMessage,
    handleDividerViewed,
    handleScrollToBottom,
    clearNewMessagesCount,
    setInitialUnreadMessage,
    clearDivider,
  }), [
    getRoomDividerState,
    handleNewMessage,
    handleUserSentMessage,
    handleDividerViewed,
    handleScrollToBottom,
    clearNewMessagesCount,
    setInitialUnreadMessage,
    clearDivider,
  ]);

  return (
    <UnreadDividerContext.Provider value={value}>
      {children}
    </UnreadDividerContext.Provider>
  );
};

export default UnreadDividerContext;
